// DJ Coach Phase 2 — client-side transition warnings.
// Mirrors the backend (/app/backend/dj_coach_phase2.py) so the UI can flag
// drastic BPM jumps, key clashes and energy jumps for any AI-generated set
// (including older sets built before warnings were saved on the playlist).

const _bpm = (t) => Number(t?.analysis?.bpm) || 0;
const _camelot = (t) => String(t?.analysis?.camelot || 'Unknown');
const _energy = (t) => {
  let raw = Number(t?.analysis?.energy) || 0;
  if (raw > 1.5) raw = raw / 100;
  return Math.max(0, Math.min(1, raw));
};

function camelotCompat(a, b) {
  if (!a || !b || a === 'Unknown' || b === 'Unknown' || a.length < 2 || b.length < 2) return 30;
  const an = parseInt(a.slice(0, -1), 10);
  const bn = parseInt(b.slice(0, -1), 10);
  const al = a.slice(-1);
  const bl = b.slice(-1);
  if (Number.isNaN(an) || Number.isNaN(bn)) return 30;
  if (a === b) return 100;
  if (an === bn && al !== bl) return 85;
  const diff = Math.min(((an - bn) % 12 + 12) % 12, ((bn - an) % 12 + 12) % 12);
  if (al === bl && diff === 1) return 80;
  if (al !== bl && diff === 1) return 55;
  if (diff === 2) return 45;
  return 25;
}

export function computeSetWarnings(tracks) {
  const out = [];
  if (!Array.isArray(tracks) || tracks.length < 2) return out;
  for (let i = 1; i < tracks.length; i++) {
    const prev = tracks[i - 1];
    const curr = tracks[i];
    const pb = _bpm(prev);
    const cb = _bpm(curr);
    if (pb > 0 && cb > 0) {
      const pct = (Math.abs(cb - pb) / pb) * 100;
      if (pct >= 6.5) {
        out.push({
          position: i,
          type: 'bpm_jump',
          severity: pct >= 9 ? 'high' : 'med',
          message: `Track ${i}→${i + 1}: BPM jumps ${pct.toFixed(1)}% (${Math.round(pb)}→${Math.round(cb)})`,
        });
      }
    }
    const pc = _camelot(prev);
    const cc = _camelot(curr);
    const harm = camelotCompat(pc, cc);
    if (harm < 50 && pc !== 'Unknown' && cc !== 'Unknown') {
      out.push({
        position: i,
        type: 'key_clash',
        severity: 'med',
        message: `Track ${i}→${i + 1}: Key clash ${pc}→${cc}`,
      });
    }
    const pe = _energy(prev);
    const ce = _energy(curr);
    if (Math.abs(pe - ce) >= 0.3) {
      const direction = ce < pe ? 'drops' : 'spikes';
      out.push({
        position: i,
        type: 'energy_jump',
        severity: 'low',
        message: `Track ${i}→${i + 1}: Energy ${direction} (${pe.toFixed(2)}→${ce.toFixed(2)})`,
      });
    }
  }
  return out;
}

export function summarizeWarnings(warnings) {
  const counts = { high: 0, med: 0, low: 0 };
  for (const w of warnings || []) counts[w.severity] = (counts[w.severity] || 0) + 1;
  return counts;
}

/**
 * Mix-quality score 0..100 derived from the warning list.
 * High = −10, Med = −4, Low = −2. Clamped to [0, 100].
 * Returns { score, verdict, tone } where `tone` is one of
 * 'green' | 'blue' | 'amber' | 'red' for UI styling.
 */
export function computeQualityScore(warnings) {
  const c = summarizeWarnings(warnings);
  const raw = 100 - c.high * 10 - c.med * 4 - c.low * 2;
  const score = Math.max(0, Math.min(100, raw));
  let verdict = 'Flawless mix';
  let tone = 'green';
  if (score < 60) {
    verdict = 'Re-roll suggested';
    tone = 'red';
  } else if (score < 75) {
    verdict = 'Listenable';
    tone = 'amber';
  } else if (score < 85) {
    verdict = 'Solid flow';
    tone = 'blue';
  } else if (score < 95) {
    verdict = 'Great flow';
    tone = 'green';
  }
  return { score, verdict, tone };
}
